'use client';

import * as React from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Download, RefreshCw, Loader2, AlertTriangle } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Subscription, SubscriptionDetail, useSubscriptionDetails } from '@/lib/api/subscriptions';
import { ErrorDisplay } from '@/components/ui/error-display';
import { parseTRPCError } from '@/lib/utils/trpc-error-utils';
import { useToast } from '@/components/ui/use-toast';
import { useSubscriptionAction } from '@/hooks/use-subscription-action';
import { useRebillSubscription } from '@/lib/api/subscriptions';
import { DateDisplay } from '@/components/date-display';
import { useFormatDate } from '@/hooks/use-format-date';

import { SubscriptionOverviewCard } from './components/subscription-overview-card';
import { SubscriptionDetailsTab } from './components/subscription-details-tab';
import { SubscriptionCustomerTab } from './components/subscription-customer-tab';
import { SubscriptionPaymentsTab } from './components/subscription-payments-tab';
import { SubscriptionDetailsSkeleton } from './components/subscription-details-skeleton';
import { SubscriptionShippingCard } from './components/subscription-shipping-card';
import { BackButton } from '@/components/ui/BackButton';
import { useOrgNavigate } from '@/hooks/use-app-navigate';
import { UpdateBillingCycleBulkActionModal } from '@/components/subscriptions/bulkAction/update-billing-cycle';
import { ChangeProcessorBulkActionModal } from '@/components/subscriptions/bulkAction/change-processor';
import { UpdatePriceBulkActionModal } from '@/components/subscriptions/bulkAction/update-price';
import { UpdateShippingRateBulkActionModal } from '@/components/subscriptions/bulkAction/update-shipping-rate';
import { UpdateQuantityBulkActionModal } from '@/components/subscriptions/bulkAction/update-quantity';
import { CancelSubscriptionModal } from '@/components/subscriptions/cancel-subscription-modal';


interface ActionButtonProps {
  show?: boolean;
  isPending: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}

const ActionButton = ({ show, isPending, onClick, icon, label }: ActionButtonProps) => {
  if (!show) return null;
  return (
    <Button variant="outline" size="sm" onClick={onClick} disabled={isPending}>
      {isPending ? (
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
      ) : (
        icon
      )}
      {label}
    </Button>
  )
}

import { withRequiredPermission } from '@/components/auth/with-required-permission';
import { PERMISSIONS } from '@/hooks/use-perm';

export default withRequiredPermission(SubscriptionDetailsPage, PERMISSIONS.SUBSCRIPTIONS_VIEW);

function SubscriptionDetailsPage() {
  const { t } = useTranslation('subscriptions');
  const { id } = useParams();
  if (!id) {
    throw new Error('Subscription ID is required');
  }

  const { data: subscription, isLoading, error } = useSubscriptionDetails(id);
  if (isLoading) {
    return <SubscriptionDetailsSkeleton />;
  }

  if (error) {
    return <ErrorDisplay error={parseTRPCError(error)} />;
  }

  if (!subscription) {
    return (
      <div className="flex flex-col space-y-4 sm:space-y-6">
        <div className="text-center">
          <h2 className="text-2xl font-semibold">{t('subscriptionNotFound')}</h2>
        </div>
      </div>
    );
  }
  return <SubscriptionDetailsPageContent subscription={subscription} />
}

function SubscriptionDetailsPageContent({ subscription }: { subscription: SubscriptionDetail }) {
  const { t } = useTranslation('subscriptions');
  const { toast } = useToast();
  const { orgRoutes } = useOrgNavigate();
  const { cancelSubscription, resumeSubscription, pauseSubscription, isCanceling, isResuming, isPausing } =
    useSubscriptionAction();
  const rebillSubscription = useRebillSubscription();
  const [isCancelModalOpen, setIsCancelModalOpen] = React.useState(false);
  const { format: formatTz, isReady: isTzReady } = useFormatDate();

  const handleCancelConfirm = React.useCallback(
    (cancelAtPeriodEnd: boolean) => {
      cancelSubscription(subscription.id, cancelAtPeriodEnd);
      setIsCancelModalOpen(false);
    },
    [cancelSubscription, subscription.id],
  );
  // Handle export
  const handleExport = React.useCallback(() => {
    if (!subscription) return;

    // Create CSV content
    const csvData = [
      ['Subscription ID', 'Status', 'Price', 'Currency', 'Customer', 'Created At', 'Next Billing'],
      [
        subscription.id,
        subscription.status,
        subscription.priceSnapshot?.currencyOptions?.[subscription.currency]?.amount || 0,
        subscription.currency || 'USD',
        `${subscription.customer?.firstName || ''} ${subscription.customer?.lastName || ''}`.trim(),
        subscription.createdAt ? formatTz(subscription.createdAt, 'yyyy-MM-dd HH:mm:ss') : 'N/A',
        subscription.nextBillingDate ? formatTz(subscription.nextBillingDate, 'yyyy-MM-dd HH:mm:ss') : 'N/A',
      ],
    ];

    const csvContent = csvData.map((row) => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `subscription-${subscription.id}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast({
      title: t('exportSuccess'),
      description: t('exportSuccessDescription'),
    });
  }, [subscription, toast, t, formatTz]);

  const status = subscription.status || 'unknown';
  const showPauseButton = status === 'active';
  const showResumeButton = status === 'paused';
  const showCancelButton = (status === 'active' || status === 'trialing' || status === 'past_due') && !subscription.cancelAtPeriodEnd;
  // Matches the billable statuses the rebill action itself accepts.
  const showRebillButton = status === 'active' || status === 'past_due';

  return (
    <div className="bg-background min-h-screen">
      <div className="flex flex-col space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-center gap-4">
            <BackButton to={orgRoutes.SUBSCRIPTIONS()} />
            <div>
              <h1 className="text-2xl font-bold tracking-tight">{t('subscriptionDetails')}</h1>
              <p className="text-muted-foreground mt-1 text-sm">
                <DateDisplay value={subscription.createdAt} />
              </p>
            </div>
          </div>
          <div className="flex flex-col items-end gap-3">
            <div className="flex flex-wrap gap-2">
              <ActionButton show={showPauseButton} isPending={isPausing} onClick={() => pauseSubscription(subscription.id)} icon={<RefreshCw className="mr-2 h-4 w-4" />} label={t('pause')} />
              <ActionButton show={showResumeButton} isPending={isResuming} onClick={() => resumeSubscription(subscription.id)} icon={<RefreshCw className="mr-2 h-4 w-4" />} label={t('resume')} />
              <ActionButton show={showRebillButton} isPending={rebillSubscription.isPending} onClick={() => rebillSubscription.mutate({ subscriptionId: subscription.id })} icon={<RefreshCw className="mr-2 h-4 w-4" />} label={t('rebill.action')} />
              <ActionButton show={showCancelButton} isPending={isCanceling} onClick={() => setIsCancelModalOpen(true)} icon={<RefreshCw className="mr-2 h-4 w-4" />} label={t('cancel')} />
              <ActionButton show={isTzReady} isPending={false} onClick={handleExport} icon={<Download className="mr-2 h-4 w-4" />} label={t('export')} />
              <UpdateBillingCycleBulkActionModal subscriptionIds={[subscription.id]} size="md" />
              <UpdatePriceBulkActionModal subscriptionIds={[subscription.id]} subscriptions={[subscription as unknown as Subscription]} size="md" />
              <ChangeProcessorBulkActionModal subscriptionIds={[subscription.id]} size="md" />
              <UpdateShippingRateBulkActionModal subscriptions={[subscription as unknown as Subscription]} size="md" />
              <UpdateQuantityBulkActionModal subscriptionIds={[subscription.id]} size="md" />
            </div>
          </div>
        </div>

        {/* Test Subscription Warning */}
        {subscription.draft && (
          <Alert variant="warning" className="mt-6">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>{t('testSubscription')}</AlertTitle>
            <AlertDescription>{t('testSubscriptionDescription')}</AlertDescription>
          </Alert>
        )}

        {/* Main Content */}
        <div className="mt-6 grid gap-6 lg:grid-cols-3">
          {/* Main content - 2/3 width */}
          <div className="space-y-6 lg:col-span-2">
            <SubscriptionOverviewCard subscription={subscription} />
            <SubscriptionDetailsTab subscription={subscription} />
            <SubscriptionPaymentsTab subscription={subscription} />
          </div>

          {/* Sidebar - 1/3 width */}
          <div className="sticky top-0 space-y-6 self-start">
            <SubscriptionCustomerTab subscription={subscription} />
            <SubscriptionShippingCard subscription={subscription} />
          </div>
        </div>
      </div>

      <CancelSubscriptionModal
        isOpen={isCancelModalOpen}
        onOpenChange={setIsCancelModalOpen}
        onConfirm={handleCancelConfirm}
        isPending={isCanceling}
      />
    </div>
  );
}
